/* eslint-disable react-hooks/rules-of-hooks */
import { FC, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { NotificationManager } from "../../services/api/NotificationManager";
import {
  GetNotificationSettingsResponse,
  UpdateNotificationSettingsRequest,
} from "../../shared/payloads/notificationSettingsPayloads";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { DropdownMenuItem, FormType } from "../../components/types";
import { Language } from "../../shared/types/websiteTypes";
import { BorderType } from "../../components/NotificationEditComponents/BorderSelect";
import { NotificationDefaultSettings } from "../../types/notification-default-settings";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { v4 } from "uuid";
import { transformUrlFilterToForms } from "../../generic-utils";
import { Branding } from "../../shared/types/commonTypes";
import Tabs from "../../components/Tabs";
import { NotificationDesignTab } from "../../components/notification/tabs/NotificationDesignTab";
import { NotificationModalBase } from "../../components/notification/NotificationModalBase";
import { NotificationPairingTab } from "../../components/notification/tabs/NotificationPairingTab";
import { addEmptyPairingForm } from "../../utils/forms/addEmptyPairingForm";
import { handlePairingFormControlChange } from "../../utils/forms/handlePairingFormControlChange";
import { onAddPairingFormExcludeChip } from "../../utils/forms/onAddPairingFormExcludeChip";
import { onAddPairingFormIncludeChip } from "../../utils/forms/onAddPairingFormIncludeChip";
import { onDeletePairingForm } from "../../utils/forms/onDeletePairingForm";
import { NotificationRulesTab } from "../../components/notification/tabs/NotificationRulesTab";

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  createdNotificationId: string;
  language: Language;
  openedFor?: "visitorSummary" | "registrationSummary" | "orderSummary";
  data?: GetNotificationSettingsResponse;
  branding: Branding;
}

const dropdownTimeRanges: DropdownMenuItem[] = [
  { title: "7 dní", value: "1" },
  { title: "měsíc", value: "2" },
  { title: "campaign.notifications.eshopRychleLastOrders.twoMonths", value: "3" },
];

type TabType = "rules" | "pairing" | "design";

const notificationStyleId = v4();

const EshopRychleLastOrdersNotificationModal: FC<SummaryModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  createdNotificationId,
  language,
  data,
  branding,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabType>("rules");
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>(
    dropdownTimeRanges[2].value,
  );
  const [withRepeat, setWithRepeat] = useState<boolean>(
    data?.repeatNotification !== undefined ? data?.repeatNotification : true,
  );
  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.backgroundColor || null,
  );
  const [textColor, setTextColor] = useState<string | null>(
    data?.notificationVisualSettings?.textColor || null,
  );
  const [borderRadius, setBorderRadius] = useState<BorderType | null>(
    data?.notificationVisualSettings?.roundness || null,
  );

  const [iconBackgroundColor, setIconBackgroundColor] = useState<string | null>(
    null,
  );
  const [backgroundOpacity, setBackgroundOpracity] = useState<number | null>(
    null,
  );
  const [active, setActive] = useState<"classic" | "small" | "compact">(
    "classic",
  );
  const [showIcon, setShowIcon] = useState<boolean>(true);
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<
    number | null
  >(null);

  const [detectedChange, setDetectedChange] = useState<boolean>(false);

  const [notificationHtml, setNotificationHtml] = useState<string>("");
  const [urlFilterForms, setUrlFilterForms] = useState<FormType[]>(
    data?.urlFilter
      ? transformUrlFilterToForms(data.urlFilter)
      : [{ control: "contains", include: ["/"], excludes: [] }],
  );

  const onCloseAndClearInputs = () => {
    setSelectedTab("rules");
    setWithRepeat(true);
    onClose();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue as TabType);
  };

  const onSave = async () => {
    try {
      const _notificationManager = NotificationManager.getManager();
      const body: UpdateNotificationSettingsRequest = {
        id: createdNotificationId,
        isActive: true,
        repeatNotification: withRepeat,
        notificationVisualSettings: {
          backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
          textColor: textColor ?? branding?.textColor ?? "",
          iconBackgroundColor:
            iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
          iconColor: branding?.iconColor ?? "",
          icon: "",
          roundness: borderRadius ?? branding?.borderRadius ?? 0,
          linkTextColor: "",
          linkButtonBackgroundColor: "",
          backgroundOpacity:
            backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
          activeSize: active ?? branding?.activeSize ?? "classic",
          isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
          iconBackgroundRadius:
            iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
        },
      };

      await _notificationManager.updateNotificationSettingsAsync(body);
      onSaveSuccess?.();
      onCloseAndClearInputs();
    } catch (e: any) {
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  useEffect(() => {
    const defaultBackgroundColor =
      data?.notificationVisualSettings?.backgroundColor ??
      branding?.backgroundColor ??
      NotificationDefaultSettings.BackgroundColor;

    const defaultForms = data?.urlFilter
      ? transformUrlFilterToForms(data.urlFilter)
      : [{ control: "contains", include: ["/"], excludes: [] }];

    const defaultTextColor =
      data?.notificationVisualSettings?.textColor ??
      branding?.textColor ??
      NotificationDefaultSettings.TextColor;

    const withRepeatDefault =
      data?.repeatNotification !== undefined ? data?.repeatNotification : true;

    const activeDefault =
      data?.notificationVisualSettings?.activeSize ?? branding?.activeSize;

    const showIconDefault =
      data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true;

    const defaultIconBackgroundColor =
      data?.notificationVisualSettings?.iconBackgroundColor ??
      branding?.iconBackgroundColor ??
      iconBackgroundColor ??
      NotificationDefaultSettings.IconBackgroundColor;

    if (
      borderRadius !== null &&
      (data?.notificationVisualSettings?.roundness !== null &&
      data?.notificationVisualSettings?.roundness !== undefined
        ? borderRadius !== data?.notificationVisualSettings?.roundness
        : borderRadius !== branding?.borderRadius)
    ) {
      setDetectedChange(true);
    } else if (
      backgroundOpacity !== null &&
      (data?.notificationVisualSettings?.backgroundOpacity !== null &&
      data?.notificationVisualSettings?.backgroundOpacity !== undefined
        ? backgroundOpacity !==
          data?.notificationVisualSettings?.backgroundOpacity
        : backgroundOpacity !== branding?.backgroundOpacity)
    ) {
      setDetectedChange(true);
    } else if (backgroundColor && backgroundColor !== defaultBackgroundColor) {
      setDetectedChange(true);
    } else if (textColor && textColor !== defaultTextColor) {
      setDetectedChange(true);
    } else if (activeDefault && activeDefault !== active) {
      setDetectedChange(true);
    } else if (showIcon !== showIconDefault) {
      setDetectedChange(true);
    } else if (
      iconBackgroundColor &&
      iconBackgroundColor !== defaultIconBackgroundColor
    ) {
      setDetectedChange(true);
    } else if (
      iconBackgroundRadius &&
      (data?.notificationVisualSettings?.iconBackgroundRadius !== null &&
      data?.notificationVisualSettings?.iconBackgroundRadius !== undefined
        ? iconBackgroundRadius !==
          data?.notificationVisualSettings?.iconBackgroundRadius
        : iconBackgroundRadius !== branding?.iconBackgroundRadius)
    ) {
      setDetectedChange(true);
    } else if (
      JSON.stringify(urlFilterForms) !== JSON.stringify(defaultForms)
    ) {
      setDetectedChange(true);
    } else if (withRepeat !== withRepeatDefault) {
      setDetectedChange(true);
    } else {
      setDetectedChange(false);
    }
  }, [
    borderRadius,
    active,
    urlFilterForms,
    withRepeat,
    textColor,
    backgroundColor,
    backgroundOpacity,
    showIcon,
    iconBackgroundColor,
    iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.roundness,
    branding?.brandingIsShowingIcon,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.activeSize,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
  ]);

  useEffect(() => {
    setBackgroundColor(
      data?.notificationVisualSettings?.backgroundColor ??
        branding?.backgroundColor ??
        null,
    );
    setTextColor(
      data?.notificationVisualSettings?.textColor ??
        branding?.textColor ??
        null,
    );
    setIconBackgroundColor(
      data?.notificationVisualSettings?.iconBackgroundColor ??
        branding?.iconBackgroundColor ??
        null,
    );
    setBorderRadius(
      data?.notificationVisualSettings?.roundness ??
        branding?.borderRadius ??
        null,
    );
    setActive(
      data?.notificationVisualSettings?.activeSize ||
        branding?.activeSize ||
        "classic",
    );
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundRadius(
      data?.notificationVisualSettings?.iconBackgroundRadius ??
        branding?.iconBackgroundRadius ??
        null,
    );
  }, [
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.isShowingIcon,
    branding?.brandingIsShowingIcon,
    data?.notificationVisualSettings?.activeSize,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconBackgroundColor,
    branding?.borderRadius,
    branding?.activeSize,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    branding?.iconBackgroundRadius,
  ]);

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            branding: {
              textColor: branding?.textColor,
              backgroundColor: branding?.backgroundColor,
              borderRadius: branding?.borderRadius,
              backgroundOpacity: branding?.backgroundOpacity,
              iconColor: branding?.iconColor,
              iconBackgroundColor: branding?.iconBackgroundColor,
              iconBackgroundRadius: branding?.iconBackgroundRadius,
              activeSize: branding?.activeSize,
              brandingIsShowingIcon: branding?.brandingIsShowingIcon,
              logoDisplay: branding?.logoDisplay,
              logo: branding?.logo,
              logoBackgroundColor: branding?.logoBackgroundColor,
              logoText: branding?.logoText,
            },
            notificationVisualSettings: {
              roundness: 1,
            },
          },
          settings: {
            type: "eshopRychleLastOrders",
            notificationVisualSettings: {
              backgroundColor:
                backgroundColor ??
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                textColor ??
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                iconBackgroundColor ??
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor: branding?.iconColor ?? "",
              icon: "",
              roundness:
                borderRadius ??
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity:
                backgroundOpacity ??
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                active ??
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                showIcon ??
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                iconBackgroundRadius ??
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
            },
          },
        },
        "eshopRychleLastOrders",
        t("campaign.notifications.lastOrders.previewText1") ?? "",
        t("campaign.notifications.eshopRychleLastOrders.screwdriver") ?? "",
        undefined,
        language,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        "https://cdn.shopify.com/static/sample-images/bath_grande.jpeg",
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        notificationStyleId,
      );
      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  });

  const onReset = () => {
    setActive(
      data?.notificationVisualSettings?.activeSize ??
        branding?.activeSize ??
        "classic",
    );
    setBackgroundColor(null);
    setTextColor(null);
    setBorderRadius(null);
    setBackgroundOpracity(null);
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundColor(null);
    setIconBackgroundRadius(null);
    setUrlFilterForms(
      data?.urlFilter
        ? transformUrlFilterToForms(data.urlFilter)
        : [
            {
              control: "beginsWith",
              include: [],
              excludes: [],
            },
          ],
    );
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
  };

  const tabs = [
    {
      value: "rules",
      label: t("campaign.notifications.settings.displayRules.title"),
      content: (
        <NotificationRulesTab
          rulesType='eshopRychleLastOrders'
          dropdownTimeRanges={dropdownTimeRanges}
          selectedTimeRange={selectedTimeRange}
          onChangeTimeRange={val => setSelectedTimeRange(val)}
          withRepeat={withRepeat}
          onToggleWithRepeat={() => setWithRepeat(!withRepeat)}
          t={t}
        />
      ),
    },
    {
      value: "pairing",
      label: t("campaign.notifications.settings.pairing.title"),
      content: (
        <NotificationPairingTab
          addEmptyPairingForm={addEmptyPairingForm}
          setForms={setUrlFilterForms}
          forms={urlFilterForms}
          forOrders={false}
          handlePairingFormControlChange={handlePairingFormControlChange}
          onAddPairingFormExcludeChip={onAddPairingFormExcludeChip}
          onAddPairingFormIncludeChip={onAddPairingFormIncludeChip}
          onDeletePairingForm={onDeletePairingForm}
        />
      ),
    },
    {
      value: "design",
      label: t("campaign.notifications.settings.design.title"),
      content: (
        <NotificationDesignTab
          active={active}
          data={data}
          backgroundColor={backgroundColor}
          backgroundOpacity={backgroundOpacity}
          borderRadius={borderRadius}
          branding={branding}
          iconBackgroundColor={iconBackgroundColor}
          iconBackgroundRadius={iconBackgroundRadius}
          setActive={setActive}
          setBackgroundColor={setBackgroundColor}
          setBackgroundOpracity={setBackgroundOpracity}
          setBorderRadius={setBorderRadius}
          setIconBackgroundColor={setIconBackgroundColor}
          setIconBackgroundRadius={setIconBackgroundRadius}
          setShowIcon={setShowIcon}
          setTextColor={setTextColor}
          showIcon={showIcon}
          textColor={textColor}
          disableIcon
        />
      ),
    },
  ];

  return (
    <NotificationModalBase
      title={t("campaign.notifications.eshopRychleLastOrders.title")}
      detectedChange={detectedChange}
      isOpen={isOpen}
      isNewNotification={!data}
      notificationHtml={notificationHtml}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      previewModalOpen={previewModalOpen}
      setPreviewModalOpen={setPreviewModalOpen}
    >
      <Tabs
        onTabChange={handleTabChange}
        selectedTab={selectedTab}
        tabs={tabs}
      />
    </NotificationModalBase>
  );
};

export default EshopRychleLastOrdersNotificationModal;
